import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import * as Sentry from "@sentry/react";
import { IconSparkles } from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import { ConfigurationAgentMessageContent } from "../../../agents/configurationAssistant/configurationAgentMessageContent.js";
import { InlineChangeCard } from "../../../agents/configurationAssistant/inlineChangeCard.js";
import { buildInteractionResolverFromMessages } from "../../../agents/interactionResolver.js";
import { useMastraChatContext } from "../../../agents/mastra/mastraChatContext.js";
import { FallbackTool } from "../../../agents/tools/fallbackTool.js";
import { GetKbAdherenceFindingTool } from "../../../agents/tools/getKbAdherenceFindingTool.js";
import { GetKnowledgeDocumentTool } from "../../../agents/tools/getKnowledgeDocumentTool.js";

const TOOL_COMPONENTS = {
  byName: {
    getKnowledgeDocumentTool: GetKnowledgeDocumentTool,
    // Pre-rename key, kept so historical threads render.
    knowledgeRetrievalTool: GetKnowledgeDocumentTool,
    getKbAdherenceFindingTool: GetKbAdherenceFindingTool,
  },
  Fallback: FallbackTool,
};

export const KnowledgeBaseAssistantChat: FC = () => {
  const chat = useMastraChatContext();
  const lastMessageId = chat.messages.at(-1)?.id;

  // Quotes and contact references in the agent's messages cite interactions
  // returned by its tool calls, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  return (
    <ChatThread
      className="flex-1"
      messages={chat.messages}
      isLoading={chat.isLoading}
      emptyState={<EmptyState onSuggestionClick={chat.sendMessage} />}
      renderMessage={(message) => (
        <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
          <ChatMessage
            message={message}
            isStreaming={chat.isRunning && message.id === lastMessageId}
            tools={TOOL_COMPONENTS}
            renderText={(args) =>
              renderKnowledgeBaseText(args, resolveInteractionId)
            }
          />
        </Sentry.ErrorBoundary>
      )}
      composer={
        <div className="border-t border-border-light bg-white p-3">
          <ChatComposer
            size={Size.SMALL}
            placeholder="Ask about this article or describe changes..."
            isRunning={chat.isRunning}
            onStop={chat.stop}
            onSend={chat.sendMessage}
          />
        </div>
      }
    />
  );
};

/** Renders a KB-agent text part, interleaving playbook operation cards. */
function renderKnowledgeBaseText(
  { text, role, messageId, isStreaming }: ChatMessageRenderTextArgs,
  resolveInteractionId: InteractionResolver,
) {
  return (
    <ConfigurationAgentMessageContent
      text={text}
      role={role}
      isStreaming={isStreaming}
      resolveInteractionId={resolveInteractionId}
      renderOperation={(segment) => (
        <InlineChangeCard
          operationId={`${messageId}-${segment.id}`}
          operation={segment.operation}
        />
      )}
    />
  );
}

const SUGGESTIONS = [
  "Summarize this article",
  "Improve clarity and structure",
  "Fix formatting issues",
] as const;

const EmptyState: FC<{ onSuggestionClick: (suggestion: string) => void }> = ({
  onSuggestionClick,
}) => {
  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100">
          <IconSparkles className="size-5 text-indigo-600" />
        </div>

        <h3 className="mt-3 text-sm font-semibold text-foreground">
          Edit with AI
        </h3>
        <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">
          I can help you improve this article, fix issues, and suggest changes.
          All changes need your approval before saving.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 pb-0">
        {SUGGESTIONS.map((suggestion) => (
          <SuggestionPill
            key={suggestion}
            onClick={() => onSuggestionClick(suggestion)}
          >
            {suggestion}
          </SuggestionPill>
        ))}
      </div>
    </div>
  );
};

const SuggestionPill: FC<{ children: string; onClick: () => void }> = ({
  children,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border border-border-light bg-white px-3 py-1.5 text-xs text-gray-700 shadow-xs transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
  >
    {children}
  </button>
);

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      layout: "agent",
      role: "assistant",
    }).bubble({ className: "bg-red-100 px-3.5" })}
  >
    Unable to render this message.
  </div>
);
